import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Search as SearchIcon } from '@mui/icons-material';
import ProfilePicture from '../../../../components/common/ProfilePicture';
import PinKeypad from '../../../../components/common/PinKeypad';

const SEARCH_FROM = 8;

/**
 * "Who is scanning?" — tap your name, then enter your PIN (or password if
 * you have no PIN). Transactions are booked to whoever logs in here.
 */
export default function StaffPicker({ staff, onPinLogin, onPasswordLogin, footer }) {
  const { t } = useTranslation(['inventory', 'auth', 'common']);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? staff.filter(s => s.name.toLowerCase().includes(q)) : staff;
  }, [staff, query]);

  const choose = (member) => {
    setSelected(member);
    setUsePassword(!member.pin_enabled);
    setPin('');
    setPassword('');
    setError(null);
  };

  const submit = async (credential) => {
    setBusy(true);
    setError(null);
    const result = usePassword
      ? await onPasswordLogin(selected, credential)
      : await onPinLogin(selected, credential);
    setBusy(false);
    if (!result.success) {
      // The server's wrong-credentials message says "username or password"
      const wrongPin = !usePassword && result.code === 'STAFF_MEMBER__INVALID_CREDS';
      setError(wrongPin ? t('inventory:scanner.device.wrong_pin') : (result.error || t('inventory:scanner.device.login_failed')));
      setPin('');
      setPassword('');
    }
  };

  if (selected) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => setSelected(null)} aria-label={t('common:buttons.back')} sx={{ width: 48, height: 48 }}>
            <BackIcon />
          </IconButton>
          <ProfilePicture profilePictureFileId={selected.profile_picture_file_id} name={selected.name} size={40} color={selected.color} />
          <Typography variant="h6" noWrap>{selected.name}</Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {usePassword ? (
          <Box
            component="form"
            onSubmit={e => { e.preventDefault(); if (password) submit(password); }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              type="password"
              label={t('auth:password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              fullWidth
            />
            <Button type="submit" variant="contained" size="large" disabled={!password || busy} sx={{ minHeight: 52 }}>
              {busy ? <CircularProgress size={24} color="inherit" /> : t('inventory:scanner.device.log_in')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography color="text.secondary">{t('inventory:scanner.device.enter_pin')}</Typography>
            {/* Full width, or the centred column shrinks the keys to their digits */}
            <Box sx={{ width: '100%' }}>
              <PinKeypad
                value={pin}
                onChange={setPin}
                disabled={busy}
                error={Boolean(error)}
                onComplete={submit}
              />
            </Box>
          </Box>
        )}

        {selected.pin_enabled && (
          <Button onClick={() => { setUsePassword(v => !v); setError(null); }} sx={{ alignSelf: 'center', minHeight: 44 }}>
            {usePassword ? t('inventory:scanner.device.use_pin') : t('inventory:scanner.device.use_password')}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, gap: 1.5 }}>
      <Typography variant="h5" fontWeight={600}>{t('inventory:scanner.device.who_is_scanning')}</Typography>
      {staff.length >= SEARCH_FROM && (
        <TextField
          placeholder={t('inventory:scanner.device.search_name')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          size="small"
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> } }}
        />
      )}
      <Box data-testid="staff-list" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {visible.map(member => (
          <ButtonBase
            key={member.staff_member_id}
            onClick={() => choose(member)}
            sx={{
              justifyContent: 'flex-start',
              gap: 1.5,
              px: 1.5,
              minHeight: 56,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <ProfilePicture profilePictureFileId={member.profile_picture_file_id} name={member.name} size={36} color={member.color} />
            <Typography variant="body1" fontWeight={500}>{member.name}</Typography>
          </ButtonBase>
        ))}
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {footer}
    </Box>
  );
}
